import { Navigate, Route, Routes, Outlet } from "react-router-dom";
import { BankIcon, BarsIcon, BuildingIcon, CalendarCheckIcon, CardIcon, GearIcon, HomeIcon, ListIcon, ReportIcon, ShieldIcon, TargetIcon, UsersIcon, WalletIcon } from "./components/Icons";
import { EntityChip } from "./components/EntityChip";
import { Shell, type NavItem } from "./components/Shell";
import { EntityProvider } from "./entity";
import { FinancialAccounts } from "./pages/FinancialAccounts";
import { Audit } from "./pages/admin/Audit";
import { ClientDetail } from "./pages/admin/ClientDetail";
import { Clients } from "./pages/admin/Clients";
import { Staff } from "./pages/admin/Staff";
import { Cards } from "./pages/Cards";
import { ClientHome } from "./pages/ClientHome";
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import { PayReceive } from "./pages/PayReceive";
import { Statements } from "./pages/Statements";
import { TxFlowProvider } from "./txflow";
import { PeriodProvider } from "./period";
import { InvestmentsHub } from "./pages/InvestmentsHub";
import { PlanningHub } from "./pages/PlanningHub";
import { PatrimonyHub } from "./pages/PatrimonyHub";
import { ReportsHub } from "./pages/ReportsHub";
import { EmpresaHub } from "./pages/EmpresaHub";
import { DocsHub } from "./pages/DocsHub";
import { REPORTS } from "./pages/reports/catalog";
import { ModuleStub } from "./pages/ModuleStub";
import { Security } from "./pages/Security";
import { Settings } from "./pages/Settings";
import { Registers } from "./pages/Registers";
import { RealmGuard, useMe } from "./session";
import type { Realm } from "./api";

/** Os nove módulos aprovados, nesta ordem (Início e Configurações nas pontas). Operacionais: Contas Financeiras (E1), Contas e Extratos e Contas a Pagar e Receber (E3); os demais seguem como estrutura de navegação. */
const MODULES = [
  { path: "contas-extratos", label: "Contas e Extratos", icon: BankIcon },
  { path: "contas-pagar-receber", label: "Contas a Pagar e Receber", icon: CalendarCheckIcon },
  { path: "contas-financeiras", label: "Contas Financeiras", icon: WalletIcon },
  { path: "cartoes", label: "Cartões de Crédito", icon: CardIcon },
  { path: "investimentos", label: "Investimentos", icon: BarsIcon },
  { path: "planejamento", label: "Planejamento Financeiro", icon: TargetIcon },
  { path: "patrimonio", label: "Patrimônio", icon: BuildingIcon },
  { path: "relatorios", label: "Relatórios", icon: ReportIcon },
] as const;

/** Menu lateral: Contas Financeiras é cadastro e fica dentro de "Cadastros" (a rota e a tela continuam as mesmas). */
const NAV_MODULES = MODULES.filter((m) => m.path !== "contas-financeiras");

/** Módulo exclusivo do ambiente Pessoa Jurídica (gestão empresarial). */
const PJ_MODULE = { path: "empresa", label: "Gestão do Negócio", icon: BuildingIcon } as const;
/** Cadastro de clientes e fornecedores (PJ): fica no menu principal, como nos demais sistemas de gestão. */
/** Comercial (PJ): orçamentos e pedidos de compra. */
const PJ_COMMERCIAL = { path: "comercial", label: "Comercial", icon: ReportIcon } as const;
const PJ_PARTNERS = { path: "empresa/parceiros", label: "Clientes e Fornecedores", icon: UsersIcon } as const;

function Area({ realm }: { realm: Realm }) {
  return (
    <RealmGuard realm={realm}>
      <PeriodProvider>{realm === "admin" ? <AreaShell realm={realm} /> : <EntityProvider><TxFlowProvider><AreaShell realm={realm} /></TxFlowProvider></EntityProvider>}</PeriodProvider>
    </RealmGuard>
  );
}

function AreaShell({ realm }: { realm: Realm }) {
  const { me, logout } = useMe();
  const base = realm === "admin" ? "/admin" : `/${realm}`;
  const nav: NavItem[] =
    realm === "admin"
      ? [
          { to: "/admin", label: "Clientes", icon: BuildingIcon, end: true },
          ...(me.user.role === "administrador_geral"
            ? [{ to: "/admin/equipe", label: "Equipe", icon: UsersIcon }, { to: "/admin/auditoria", label: "Auditoria", icon: ListIcon }]
            : []),
          { to: "/admin/seguranca", label: "Segurança", icon: ShieldIcon },
        ]
      : [
          { to: base, label: "Início", icon: HomeIcon, end: true },
          ...(realm === "pj" ? [NAV_MODULES.slice(0, 6), [PJ_MODULE, PJ_COMMERCIAL], NAV_MODULES.slice(6)] : [NAV_MODULES]).flat().map((m) => ({ to: `${base}/${m.path}`, label: m.label, icon: m.icon,
            ...(m.path === "investimentos" ? { children: [
              { to: `${base}/investimentos`, label: "Visão geral", end: true }, { to: `${base}/investimentos/carteira`, label: "Carteira" },
              { to: `${base}/investimentos/movimentacoes`, label: "Movimentações" }, { to: `${base}/investimentos/resultados`, label: "Resultados" },
              { to: `${base}/investimentos/notas`, label: "Notas" }] } : {}),
            ...(m.path === "relatorios" ? { children: [{ to: `${base}/relatorios`, label: "Todos os relatórios", end: true }, ...REPORTS.map((r) => ({ to: `${base}/relatorios/${r.slug}`, label: r.title }))] } : {}),
            ...(m.path === "empresa" ? { children: [
              { to: `${base}/empresa/dre`, label: "DRE" }, { to: `${base}/empresa/dfc`, label: "DFC (fluxo de caixa)" }, { to: `${base}/empresa/indicadores`, label: "Indicadores" },
              { to: `${base}/empresa/mapa`, label: "Mapa do DRE" }] } : {}),
            ...(m.path === "comercial" ? { children: [
              { to: `${base}/comercial/orcamentos`, label: "Orçamentos" }, { to: `${base}/comercial/pedidos`, label: "Pedidos de compra" }] } : {}),
            ...(m.path === "patrimonio" ? { children: [
              { to: `${base}/patrimonio/bens`, label: "Bens" }, { to: `${base}/patrimonio/financiamentos`, label: "Empréstimos e financiamentos" }] } : {}),
            ...(m.path === "planejamento" ? { children: [
              { to: `${base}/planejamento/orcamento`, label: "Orçamento" }, { to: `${base}/planejamento/metas`, label: "Metas" },
              { to: `${base}/planejamento/projecao`, label: "Projeção de caixa" }, { to: `${base}/planejamento/alocacao`, label: "Alocação da carteira" }] } : {}) })),
          { to: `${base}/cadastros`, label: "Cadastros", icon: ListIcon, children: [
            { to: `${base}/contas-financeiras`, label: "Contas Financeiras" }, { to: `${base}/cadastros/categorias`, label: "Categorias" }, { to: `${base}/cadastros/contrapartes`, label: "Beneficiários e Pagadores" },
            ...(realm === "pj" ? [{ to: `${base}/empresa/parceiros`, label: "Clientes e Fornecedores" }, { to: `${base}/empresa/centros`, label: "Centros de custo e projetos" }] : []),
            { to: `${base}/cadastros/memorizacao`, label: "Memorização" }] },
          { to: `${base}/configuracoes`, label: "Configurações", icon: GearIcon },
        ];
  const contextLabel = realm === "admin" ? "Administração MM Money" : me.tenant?.kind === "pf" ? "Pessoa Física" : "Pessoa Jurídica";
  return (
    <Shell nav={nav} realm={realm} contextLabel={contextLabel} entity={realm === "pj" ? <EntityChip /> : undefined} displayName={me.user.displayName} email={me.user.email} settingsTo={realm === "admin" ? "/admin/seguranca" : `${base}/configuracoes`} onLogout={logout}>
      <Outlet />
    </Shell>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      {(["pf", "pj"] as const).map((r) => [
        <Route key={r + "e"} path={`/${r}/entrar`} element={<Login realm={r} />} />,
        <Route key={r + "c"} path={`/${r}/cadastro`} element={<Register realm={r} />} />,
        <Route key={r + "f"} path={`/${r}/esqueci-senha`} element={<ForgotPassword realm={r} />} />,
        <Route key={r + "r"} path={`/${r}/redefinir-senha`} element={<ResetPassword realm={r} />} />,
        <Route key={r} path={`/${r}`} element={<Area realm={r} />}>
          <Route index element={<ClientHome />} />
          {MODULES.map((m) => <Route key={m.path} path={m.path === "investimentos" || m.path === "planejamento" || m.path === "patrimonio" || m.path === "relatorios" ? `${m.path}/*` : m.path} element={m.path === "contas-financeiras" ? <FinancialAccounts /> : m.path === "contas-extratos" ? <Statements /> : m.path === "contas-pagar-receber" ? <PayReceive /> : m.path === "cartoes" ? <Cards /> : m.path === "investimentos" ? <InvestmentsHub /> : m.path === "planejamento" ? <PlanningHub /> : m.path === "patrimonio" ? <PatrimonyHub /> : m.path === "relatorios" ? <ReportsHub /> : <ModuleStub title={(m as { label: string }).label} />} />)}
          {r === "pj" && <Route path="empresa/*" element={<EmpresaHub />} />}
          {r === "pj" && <Route path="comercial/*" element={<DocsHub />} />}
          <Route path="cadastros/*" element={<Registers />} />
          <Route path="configuracoes" element={<Settings />} />
          <Route path="seguranca" element={<Navigate to={`/${r}/configuracoes`} replace />} />
        </Route>,
      ])}
      <Route path="/admin/entrar" element={<Login realm="admin" />} />
      <Route path="/admin" element={<Area realm="admin" />}>
        <Route index element={<Clients />} />
        <Route path="clientes/:id" element={<ClientDetail />} />
        <Route path="equipe" element={<Staff />} />
        <Route path="auditoria" element={<Audit />} />
        <Route path="seguranca" element={<Security />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
